<script>
  import electionData from '$lib/data/election-data.json';
  import {
    blocMeta as defaultBlocs,
    partyMeta as defaultParties,
    partyNotes as defaultPartyNotes,
    labelSettings as defaultLabelSettings,
    connectionSettings as defaultConnectionSettings,
    coalitionSettings as defaultCoalitionSettings,
    oppositionSettings as defaultOppositionSettings,
    electionAnnotations as defaultElectionAnnotations,
  } from './party-meta.js';

  export let data = electionData;
  export let startYear = 2009;
  // 'asc' reads as a timeline (oldest row on top); 'desc' puts the most
  // recent row on top — useful when only comparing a couple of elections.
  export let sortOrder = 'asc';
  export let blocMeta = defaultBlocs;
  export let partyMeta = defaultParties;
  // Per-election label/tooltip overrides (e.g. "RZ & Jewish Power" for
  // Religious Zionism's 2021/2022 rows) — keyed by election date then exact
  // party name. `ElectionComparison` passes `{}` to opt out for its own rows.
  export let partyNotes = defaultPartyNotes;
  export let labelSettings = defaultLabelSettings;
  export let connectionSettings = defaultConnectionSettings;
  export let coalitionSettings = defaultCoalitionSettings;
  export let oppositionSettings = defaultOppositionSettings;
  export let electionAnnotations = defaultElectionAnnotations;
  export let title = '';
  export let dek = '';
  export let source = '';
  export let notes = '';
  export let altText = '';
  // 'top' (default) keeps the legend under the title/dek, above the chart —
  // used by the timeline view. ElectionComparison instead wants the key
  // read after the bars, so it passes 'bottom'.
  export let legendPosition = 'top';
  // Bolds the 60-seat gridline/labels to flag the majority threshold —
  // opt-in since it's only meaningful when comparing against the 120-seat
  // Knesset (e.g. ElectionComparison), not every use of this chart.
  export let markMajority = false;
  // Vertical space between election rows — bump this up when a row carries
  // an opposition/coalition label so it doesn't crowd the ribbons or the
  // next row's bar.
  export let rowGap = 35;
  // Vertical space between the top axis labels and the first election row —
  // bump this up when that row's year label carries a long annotation (e.g.
  // ElectionComparison's "Estimate based on latest polling average") so it
  // doesn't crowd the axis numbers above it.
  export let topPadding = 48;
  // Fills any gap between a row's plotted seats and all 120 Knesset seats
  // with a light gray "unassigned" segment (see `blocMeta.unassigned` in
  // `party-meta.js`) — e.g. when a party's seat estimate is dropped below a
  // reporting threshold. Off by default: real election results already sum
  // to 120, so there's nothing to fill.
  export let fillRemainder = false;
  export let remainderLabel = 'Unassigned';
  // Explains the gray "unassigned" segment as its own line right under the
  // legend, since that bloc is deliberately left out of the legend's chips
  // (it's a chart-filler, not a political grouping). Empty by default — no
  // fillRemainder use passes it.
  export let unassignedNote = '';

  let chartWidth = 1000;
  let activeTooltip = null;
  $: W = chartWidth;
  const top = topPadding;
  const rowH = 31;
  $: plotW = W;
  $: x = (seats) => (seats / 120) * plotW;

  const fallback = (party) => ({
    bloc: 'other',
    family: party,
    short: party,
    fullName: party,
  });
  const labelFor = (date, year) => {
    const sameYear = data
      .filter((d) => d.year === year)
      .some((d) => d.date !== date);
    if (!sameYear) return String(year);
    // Two elections in one year read as "2019, April" / "2019, September".
    const month = new Intl.DateTimeFormat('en', { month: 'long' }).format(
      new Date(`${date}T00:00:00Z`)
    );
    return `${year}, ${month}`;
  };

  function prepare(rows) {
    const grouped = new Map();
    rows
      .filter((d) => d.year >= startYear && d.seats > 0)
      .forEach((d) => {
        if (!grouped.has(d.date)) grouped.set(d.date, []);
        grouped.get(d.date).push(d);
      });

    return [...grouped.entries()]
      .sort(([a], [b]) =>
        sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b)
      )
      .map(([date, parties], electionIndex) => {
        let running = 0;
        const withMeta = parties.map((d) => {
          const meta = partyMeta[d.party] || fallback(d.party);
          const override = partyNotes[date]?.[d.party];
          return {
            ...d,
            meta: override ? { ...meta, ...override } : meta,
          };
        });
        if (fillRemainder) {
          const total = withMeta.reduce((sum, d) => sum + d.seats, 0);
          const remainder = 120 - total;
          if (remainder > 0) {
            withMeta.push({
              party: remainderLabel,
              year: withMeta[0]?.year,
              seats: remainder,
              meta: {
                bloc: 'unassigned',
                family: remainderLabel,
                short: remainderLabel,
                // Sits between `right`'s unweighted members (e.g. Yisrael
                // Beitenu, weight 0) and Likud (weight 1) — see
                // `comparisonBlocMeta.unassigned` in `party-meta.js`.
                weight: 0.5,
              },
            });
          }
        }
        const sorted = withMeta
          .sort(
            (a, b) =>
              (blocMeta[a.meta.bloc]?.order ?? 99) -
                (blocMeta[b.meta.bloc]?.order ?? 99) ||
              (a.meta.weight ?? 0) - (b.meta.weight ?? 0) ||
              a.meta.family.localeCompare(b.meta.family) ||
              b.seats - a.seats
          )
          .map((d) => {
            const placed = { ...d, x0: running, x1: running + d.seats };
            running += d.seats;
            return placed;
          });
        return {
          date,
          year: sorted[0]?.year,
          label: labelFor(date, sorted[0]?.year),
          y: top + electionIndex * (rowH + rowGap),
          parties: sorted,
        };
      });
  }

  function familyBands(election) {
    const groups = new Map();
    election.parties.forEach((d) => {
      const key = d.meta.family;
      const current = groups.get(key) || {
        key,
        x0: d.x0,
        x1: d.x1,
        bloc: d.meta.bloc,
      };
      current.x0 = Math.min(current.x0, d.x0);
      current.x1 = Math.max(current.x1, d.x1);
      groups.set(key, current);
    });
    return groups;
  }

  function makeRibbons(elections, plotWidth) {
    const scale = (seats) => (seats / 120) * plotWidth;
    return elections.slice(0, -1).flatMap((from, i) => {
      const to = elections[i + 1];
      const a = familyBands(from);
      const b = familyBands(to);
      return [...a.values()]
        .filter((d) => b.has(d.key))
        .map((d) => {
          const target = b.get(d.key);
          const y0 = from.y + rowH;
          const y1 = to.y;
          const mid = (y0 + y1) / 2;
          return {
            key: `${from.date}-${to.date}-${d.key}`,
            bloc: target.bloc,
            path: `M ${scale(d.x0)} ${y0} C ${scale(d.x0)} ${mid}, ${scale(target.x0)} ${mid}, ${scale(target.x0)} ${y1} L ${scale(target.x1)} ${y1} C ${scale(target.x1)} ${mid}, ${scale(d.x1)} ${mid}, ${scale(d.x1)} ${y0} Z`,
          };
        });
    });
  }

  $: fits = (d) => {
    if (labelSettings.hide.includes(d.party)) return false;
    if (labelSettings.show.includes(d.party)) return true;
    if (labelSettings.mode === 'selected') return false;
    if (labelSettings.mode === 'all') return true;
    if (d.meta.short.length > labelSettings.maxCharacters) return false;
    const minSeats =
      W < labelSettings.mobileBreakpoint ?
        labelSettings.mobileMinSeats
      : labelSettings.minSeats;
    const availablePixels = x(d.x1) - x(d.x0) - 8;
    const estimatedLabelWidth =
      d.meta.short.length * labelSettings.pixelsPerCharacter;
    return d.seats >= minSeats && estimatedLabelWidth <= availablePixels;
  };

  // Greedy word-wrap for the tooltip's optional note line — matches the
  // ~34-characters-per-line estimate `tooltipLayout` sizes the box around.
  function wrapText(text, maxChars = 34) {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (candidate.length > maxChars && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
    return lines;
  }

  function showTooltip(party, election) {
    activeTooltip = { party, election };
  }

  function coalitionBounds(election) {
    if (
      !coalitionSettings.show ||
      election.date !== coalitionSettings.electionDate
    )
      return null;
    const members = election.parties.filter((party) =>
      coalitionSettings.parties.includes(party.party)
    );
    if (!members.length) return null;
    return {
      x0: Math.min(...members.map((party) => party.x0)),
      x1: Math.max(...members.map((party) => party.x1)),
    };
  }

  function oppositionBounds(election) {
    if (
      !oppositionSettings.show ||
      election.date !== oppositionSettings.electionDate
    )
      return null;
    // `parties` (exact party names) takes precedence when given — lets a
    // consumer flag a specific curated alliance rather than every party in a
    // political bloc, same distinction `coalitionSettings.parties` already
    // makes above.
    const members = election.parties.filter((party) =>
      oppositionSettings.parties ?
        oppositionSettings.parties.includes(party.party)
      : oppositionSettings.blocs.includes(party.meta.bloc)
    );
    if (!members.length) return null;
    return {
      x0: Math.min(...members.map((party) => party.x0)),
      x1: Math.max(...members.map((party) => party.x1)),
    };
  }

  $: tooltipLayout = ({ party, election }) => {
    const pad = 10;
    const tip = 8;
    // Base box fits 3 lines (election, party, seat count); a `meta.note`
    // (e.g. the Religious Zionism/Jewish Power tooltip) adds a 4th, wrapped
    // across as many lines as it needs at ~34 characters per line.
    const noteLines =
      party.meta.note ? Math.ceil(party.meta.note.length / 34) : 0;
    const boxH = 62 + noteLines * 15;
    const electionLine = `${election.label} election`;
    const longestLine = Math.max(
      party.party.length,
      electionLine.length,
      party.meta.note ? Math.min(party.meta.note.length, 34) : 0
    );
    const desiredW = Math.max(150, longestLine * 6.25 + pad * 2);
    const boxW = Math.min(desiredW, W - 12);
    const anchorX = (x(party.x0) + x(party.x1)) / 2;
    const anchorY = election.y;
    const above = anchorY - boxH - tip >= 4;
    const boxY = above ? anchorY - boxH - tip : election.y + rowH + tip;
    const boxX = Math.max(6, Math.min(anchorX - boxW / 2, W - boxW - 6));
    const tipX = Math.max(boxX + 12, Math.min(anchorX, boxX + boxW - 12));
    const tipHalf = 7;
    const boxBottom = boxY + boxH;
    const path =
      above ?
        `M ${boxX} ${boxY} H ${boxX + boxW} V ${boxBottom} H ${tipX + tipHalf} L ${anchorX} ${anchorY} L ${tipX - tipHalf} ${boxBottom} H ${boxX} Z`
      : `M ${boxX} ${boxY} H ${tipX - tipHalf} L ${anchorX} ${election.y + rowH} L ${tipX + tipHalf} ${boxY} H ${boxX + boxW} V ${boxBottom} H ${boxX} Z`;
    return { boxX, boxY, boxW, boxH, path };
  };

  $: elections = prepare(data);
  $: ribbons = makeRibbons(elections, plotW);
  $: bottomAxisY =
    elections.length ? elections[elections.length - 1].y + rowH + 25 : top + 25;
  $: height = bottomAxisY + 10;
  // 'unassigned' is a chart-filler bloc (see `fillRemainder`), not a real
  // political grouping — it gets its own explanatory line via
  // `unassignedNote` instead of a legend chip.
  $: legend = Object.entries(blocMeta).filter(
    ([key]) =>
      key !== 'unassigned' &&
      elections.some((e) => e.parties.some((d) => d.meta.bloc === key))
  );
</script>

<figure class="election-flow" aria-labelledby="election-flow-title">
  <figcaption>
    <h2 id="election-flow-title">{title}</h2>
    <p>{dek}</p>
  </figcaption>

  {#if legendPosition === 'top'}
    <div class="legend" aria-label="Political grouping legend">
      {#each legend as [, item]}
        <span><i style:background={item.color}></i>{item.label}</span>
      {/each}
    </div>
  {/if}

  <div class="scroller" bind:clientWidth={chartWidth}>
    <svg
      width={W}
      {height}
      role="img"
      aria-label={altText}
      on:click={() => (activeTooltip = null)}
    >
      <g class="axis">
        {#each [0, 20, 40, 60, 80, 100, 120] as tick}
          {@const isMajority = markMajority && tick === 60}
          <line
            x1={x(tick)}
            x2={x(tick)}
            y1={top - 22}
            y2={bottomAxisY - 13}
            class:majority-line={isMajority}
          />
          <text
            x={x(tick)}
            y={top - 30}
            text-anchor={tick === 0 ? 'start'
            : tick === 120 ? 'end'
            : 'middle'}
            class:majority-label={isMajority}>{tick}</text
          >
          <text
            x={x(tick)}
            y={bottomAxisY}
            text-anchor={tick === 0 ? 'start'
            : tick === 120 ? 'end'
            : 'middle'}
            class:majority-label={isMajority}>{tick}</text
          >
        {/each}
      </g>

      {#if connectionSettings.show}
        <g
          class="ribbons"
          aria-hidden="true"
          style:--ribbon-opacity={connectionSettings.opacity}
        >
          {#each ribbons as ribbon (ribbon.key)}
            <path
              d={ribbon.path}
              fill={blocMeta[ribbon.bloc]?.color || blocMeta.other.color}
            />
          {/each}
        </g>
      {/if}

      {#each elections as election (election.date)}
        {@const coalition = coalitionBounds(election)}
        {@const opposition = oppositionBounds(election)}
        <g class="election">
          <text class="year" x={0} y={election.y - 6} text-anchor="start">
            <tspan class="year-value">{election.label}</tspan>
            {#if electionAnnotations[election.date]}
              <tspan class="year-note" dx="6"
                >{electionAnnotations[election.date]}</tspan
              >
            {/if}
          </text>
          {#each election.parties as party (party.party)}
            <g>
              <rect
                x={x(party.x0)}
                y={election.y}
                width={x(party.x1) - x(party.x0)}
                height={rowH}
                fill={blocMeta[party.meta.bloc]?.color || blocMeta.other.color}
                role="button"
                tabindex="0"
                aria-label={`${party.party}, ${party.seats} seats in the ${election.label} election`}
                on:mouseenter={() => showTooltip(party, election)}
                on:mouseleave={() => (activeTooltip = null)}
                on:focus={() => showTooltip(party, election)}
                on:blur={() => (activeTooltip = null)}
                on:click|stopPropagation={() => showTooltip(party, election)}
                on:keydown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    showTooltip(party, election);
                  }
                }}
              />
              {#if fits(party)}
                <text
                  x={(x(party.x0) + x(party.x1)) / 2}
                  y={election.y + rowH / 2}
                  text-anchor="middle"
                  dominant-baseline="middle"
                  class:on-dark={party.meta.bloc === 'farRight'}
                  >{party.meta.short}</text
                >
              {/if}
            </g>
          {/each}
          {#if coalition}
            <text
              class="coalition-label"
              x={x(coalition.x1)}
              y={election.y - 6}
              text-anchor="end">Netanyahu's 2022 coalition government</text
            >
            <rect
              class="coalition-outline"
              x={x(coalition.x0)}
              y={election.y}
              width={x(coalition.x1) - x(coalition.x0)}
              height={rowH}
              fill="none"
              pointer-events="none"
              style:--coalition-stroke={coalitionSettings.stroke}
              style:--coalition-stroke-width={coalitionSettings.strokeWidth}
            />
          {/if}
          {#if opposition}
            <rect
              class="opposition-outline"
              x={x(opposition.x0)}
              y={election.y}
              width={x(opposition.x1) - x(opposition.x0)}
              height={rowH}
              fill="none"
              pointer-events="none"
              style:--opposition-stroke={oppositionSettings.stroke}
              style:--opposition-stroke-width={oppositionSettings.strokeWidth}
              style:--opposition-dash={oppositionSettings.dash}
            />
            <text
              class="opposition-label"
              x={(x(opposition.x0) + x(opposition.x1)) / 2}
              y={election.y + rowH + 16}
              text-anchor="middle">{oppositionSettings.label}</text
            >
          {/if}
        </g>
      {/each}

      {#if activeTooltip}
        {@const tip = tooltipLayout(activeTooltip)}
        <g
          class="tooltip"
          role="tooltip"
          aria-live="polite"
          pointer-events="none"
        >
          <path d={tip.path} />
          <text x={tip.boxX + 10} y={tip.boxY + 19}>
            <tspan>{activeTooltip.election.label} election</tspan>

            <tspan class="tooltip-party" x={tip.boxX + 10} dy="17"
              >{activeTooltip.party.meta.fullName}</tspan
            >
            <tspan x={tip.boxX + 10} dy="17"
              >{activeTooltip.party.seats} seats</tspan
            >
            {#if activeTooltip.party.meta.note}
              {#each wrapText(activeTooltip.party.meta.note) as line}
                <tspan class="tooltip-note" x={tip.boxX + 10} dy="15"
                  >{line}</tspan
                >
              {/each}
            {/if}
          </text>
        </g>
      {/if}
    </svg>
  </div>

  {#if legendPosition === 'bottom'}
    <div class="legend legend--bottom" aria-label="Political grouping legend">
      {#each legend as [, item]}
        <span><i style:background={item.color}></i>{item.label}</span>
      {/each}
    </div>
    {#if unassignedNote}
      <p class="unassigned-note">
        <i style:background={blocMeta.unassigned?.color}></i><span
          >{unassignedNote}</span
        >
      </p>
    {/if}
  {/if}

  {#if notes}
    <p class="note">{@html notes}</p>
  {/if}
  <p class="source">{source}</p>
</figure>

<style lang="scss">
  @use '@reuters-graphics/graphics-components/dist/scss/mixins' as mixins;

  figure.election-flow {
    box-sizing: border-box;
    margin: 0 auto;
    padding: 0;
    width: 100%;
    color: #222;
    @include mixins.font-knowledge;
  }
  figcaption {
    h2 {
      margin: 0;
      @include mixins.text-lg;
      @include mixins.leading-tighter;
    }
    p {
      margin: 0;
      @include mixins.font-knowledge;
      @include mixins.text-sm;
      @include mixins.fmt-1;
      color: #666;
    }
  }
  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 7px 16px;
    @include mixins.fmt-2;
    @include mixins.fmb-1;
    @include mixins.text-xs;
    @include mixins.font-regular;
    span {
      display: inline-flex;
      align-items: center;
    }
    i {
      width: 11px;
      height: 11px;
      margin-right: 5px;
    }
  }
  // extra breathing room below a bottom-positioned legend (e.g. ElectionComparison)
  // before the notes/source that follow it
  .legend--bottom {
    @include mixins.fmb-1;
  }
  .unassigned-note {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    @include mixins.font-knowledge;
    @include mixins.text-xs;
    @include mixins.fmb-2;
    margin-inline: 0;
    margin-block-start: 0;
    line-height: 1.35;
    color: #777;

    i {
      flex: 0 0 auto;
      width: 11px;
      height: 11px;
      margin-top: 2px;
    }
  }
  .scroller {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    width: 100%;
    // was `overflow-x: hidden` — that clipped the svg's own `overflow: visible`
    // below (an ancestor's hidden overflow always wins over a descendant's),
    // cutting off thick strokes/labels near the plot edges.
    overflow: visible;
  }
  svg {
    display: block;
    margin: 0;
    padding: 0;
    overflow: visible;
  }
  .axis {
    line {
      stroke: #ddd;
      stroke-width: 0.75;
    }
    line.majority-line {
      stroke: #111;
      stroke-width: 1.5;
    }
    text {
      @include mixins.font-knowledge;
      fill: #666;
      @include mixins.text-xxs;
    }
    text.majority-label {
      fill: #111;
      @include mixins.font-bold;
    }
  }
  .ribbons path {
    opacity: var(--ribbon-opacity);
  }
  .election rect {
    stroke: #fff;
    stroke-width: 1;
  }
  .election rect.coalition-outline {
    stroke: var(--coalition-stroke);
    stroke-width: var(--coalition-stroke-width);
    vector-effect: non-scaling-stroke;
  }
  .election rect.opposition-outline {
    stroke: var(--opposition-stroke);
    stroke-width: var(--opposition-stroke-width);
    stroke-dasharray: var(--opposition-dash);
    vector-effect: non-scaling-stroke;
  }
  .election text.opposition-label {
    @include mixins.text-xs;
    @include mixins.font-bold;
    fill: #333;
  }
  .election rect[role='button'] {
    cursor: pointer;
  }
  .election text {
    @include mixins.font-knowledge;
    pointer-events: none;
    @include mixins.text-xs;
    fill: #171717;
  }
  // Far-right rows use a dark blue fill (`chartColors.farRight`) — the
  // default label colour above is unreadable on it.
  .election text.on-dark {
    fill: #fff;
  }
  .election text.year {
    @include mixins.text-sm;
    @include mixins.font-regular;
    fill: #333;
  }
  .year-value {
    @include mixins.font-bold;
  }
  .year-note {
    @include mixins.font-regular;
  }
  .election text.coalition-label {
    @include mixins.text-xs;
    @include mixins.font-bold;
    fill: #333;
  }

  .tooltip path {
    fill: #fff;
    // stroke: #111;
    // stroke-width: 1;
    filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.4));
  }
  .tooltip text {
    @include mixins.font-knowledge;
    fill: #111;
    @include mixins.text-xs;
  }
  .tooltip-party {
    @include mixins.font-bold;
  }
  .tooltip-note {
    @include mixins.text-xxs;
    fill: #666;
  }
  .note,
  .source {
    @include mixins.font-knowledge;
    @include mixins.text-xs;
    @include mixins.fmt-1;
    margin-inline: 0;
    margin-block-end: 0;
    line-height: 1.35;
    color: #777;
  }
  @media (max-width: 600px) {
    figcaption h2 {
      @include mixins.text-base;
    }
    .legend {
      gap: 6px 12px;
    }
  }
</style>
